import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useHref } from "react-router";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { logApplicationFailure } from "@/lib/user-error";

// Share button with dropdown (not a modal/blur): opens a small popover anchored
// to the button with a copy-able URL. `path` is the app-relative path; the
// absolute URL is built from the router basename + window.origin. The popover
// is portalled with fixed positioning so it isn't clipped by overflow-hidden
// containers. Trigger label/icon configurable via `children` + `className`.

interface ShareUrlButtonProps {
  path: string;
  label?: string;
  title?: string;
  className?: string;
  children?: ReactNode;
}

export function ShareUrlButton({
  path,
  label,
  title,
  className = "",
  children,
}: ShareUrlButtonProps) {
  const intl = useIntl();
  const labelText = label ?? intl.formatMessage({ id: "design.shareWithArrow", defaultMessage: "↗ Share" });
  const titleText = title ?? intl.formatMessage({ id: "design.shareTitle", defaultMessage: "Share" });
  const href = useHref(path);
  const url = typeof window !== "undefined" ? window.location.origin + href : href;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (
        btnRef.current?.contains(e.target as Node) ||
        panelRef.current?.contains(e.target as Node)
      ) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    const close = () => setOpen(false);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  async function copy() {
    try {
      // Ohne sicheren Kontext (http auf einem anderen Host als localhost) gibt es
      // keine Clipboard-API. Der Optional-Chain schluckte das und meldete Erfolg,
      // obwohl nichts kopiert wurde.
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      logApplicationFailure("share.copy-url");
    }
  }

  return (
    <>
      <button
        ref={btnRef}
        id="share-url"
        type="button"
        title={titleText}
        aria-label={titleText}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
        className={className}
      >
        {children ?? labelText}
      </button>

      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              id="share-url-panel"
              role="dialog"
              style={{ position: "fixed", top: pos.top, right: pos.right }}
              className="z-[1000] w-72 rounded-md bg-surface p-3 shadow-xl ring-1 ring-ink/10"
            >
              <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">{intl.formatMessage({ id: "design.shareLink", defaultMessage: "Share link" })}</div>
              <div className="flex items-center gap-2">
                <input
                  id="share-url-value"
                  readOnly
                  value={url}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 rounded-md bg-page px-2 py-1.5 text-smycles-meta outline-none ring-1 ring-ink/10"
                />
                <Button
                  id="share-url-copy"
                  variant="primary"
                  size="sm"
                  onClick={copy}
                  className="shrink-0"
                >
                  {copied
                    ? intl.formatMessage({ id: "design.copied", defaultMessage: "Copied" })
                    : intl.formatMessage({ id: "design.copy", defaultMessage: "Copy" })}
                </Button>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
